import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ShieldCheck, IndianRupee, HeartHandshake, Award,
  Pill, FlaskConical, Sparkles, Building2, Target,
} from "lucide-react";
import { SITE, PILLARS, PRODUCTS, THERAPIES } from "../data/siteData";

const pillarIcons = [ShieldCheck, IndianRupee, HeartHandshake];
const oncologyBrands = PRODUCTS.filter((p) => p.category === "Oncology");
const featuredProducts = PRODUCTS.filter((p) => p.image).slice(0, 4);

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55 },
};

const STATS = [
  { value: "2007", label: "Founded in Kolkata" },
  { value: "1st", label: "Branded oncology house in Eastern India" },
  { value: "7+", label: "Therapeutic areas" },
  { value: "15+", label: "Branded formulations" },
];

const Home = () => (
  <div data-testid="home-page">
    {/* Hero */}
    <section className="relative overflow-hidden bg-[#0A1633] text-white" data-testid="home-hero">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, #93C5FD 1px, transparent 0)",
          backgroundSize: "30px 30px",
        }}
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-28">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-blue-400/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-blue-200"
            data-testid="hero-badge"
          >
            <Award size={14} /> Since {SITE.founded} · Kolkata
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl"
            data-testid="hero-heading"
          >
            {SITE.tagline}
            <span className="mt-4 block text-2xl font-bold text-blue-300 sm:text-3xl lg:text-4xl">
              Pioneering Oncology Care in Eastern India
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subtext"
          >
            The first ever branded oncology molecule pharmaceutical house in Eastern India —
            now growing across gastroenterology, antibiotics, cardio-diabetic care,
            neuropsychiatry and nutraceuticals, in tie-up with world-class leading manufacturers.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link
              to="/products"
              data-testid="hero-explore-products-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/40 transition-colors duration-200 hover:bg-[#3B82F6]"
            >
              Explore Our Medicines <ArrowRight size={16} />
            </Link>
            <Link
              to="/about"
              data-testid="hero-about-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/10"
            >
              Our Story
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="relative"
        >
          <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
            <img
              src="/assets/products/page-1.jpg"
              alt="Cytocare Formulations — Caring Life and Beyond"
              className="h-full w-full object-cover"
              data-testid="hero-image"
            />
          </div>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-slate-800 shadow-lg" data-testid="hero-legacy-card">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#1D4ED8]">Legacy</p>
            <p className="mt-1 text-sm font-bold">Trusted in Oncology, Biosimilars & Injectable Antibiotics</p>
          </div>
        </motion.div>
      </div>

      {/* Stats bar */}
      <div className="relative border-t border-white/10 bg-white/5 backdrop-blur-sm" data-testid="hero-stats">
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-white/10 lg:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} className="px-6 py-7">
              <p className="text-3xl font-extrabold text-white">{s.value}</p>
              <p className="mt-1 text-xs text-slate-300 sm:text-sm">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Pillars */}
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="home-pillars">
      <motion.div {...fadeUp} className="max-w-2xl">
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#1D4ED8]">Our Foundation</p>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          Built on three core pillars
        </h2>
      </motion.div>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {PILLARS.map((p, i) => {
          const Icon = pillarIcons[i];
          return (
            <motion.div
              key={p.title}
              {...fadeUp}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              className="group rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition-shadow duration-300 hover:shadow-xl"
              data-testid={`pillar-card-${i}`}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-[#1D4ED8] transition-colors duration-300 group-hover:bg-[#1D4ED8] group-hover:text-white">
                <Icon size={22} />
              </div>
              <h3 className="mt-6 text-xl font-bold text-slate-900">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.desc}</p>
            </motion.div>
          );
        })}
      </div>
    </section>

    {/* Oncology spotlight */}
    <section className="bg-[#F2F7FB] py-24" data-testid="home-oncology-spotlight">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#1D4ED8]">Oncology Heritage</p>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              From the makers of trusted antineoplastic brands
            </h2>
          </div>
          <Link
            to="/products"
            data-testid="oncology-view-all-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1D4ED8] transition-colors hover:text-[#173CAE]"
          >
            View all products <ArrowRight size={16} />
          </Link>
        </motion.div>
        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7">
          {oncologyBrands.map((b, i) => (
            <motion.div
              key={b.id}
              {...fadeUp}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-center shadow-sm transition-shadow duration-300 hover:shadow-lg"
              data-testid={`oncology-brand-${b.id}`}
            >
              <p className="text-sm font-extrabold tracking-wide text-[#1D4ED8]">{b.brand}</p>
              <p className="mt-1.5 text-[11px] leading-snug text-slate-500">{b.composition}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Featured products from visual aid */}
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="home-featured-products">
      <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-2xl">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#1D4ED8]">Product Portfolio</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Beyond oncology — everyday care brands
          </h2>
        </div>
        <Link
          to="/products"
          data-testid="featured-view-all-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#1D4ED8] transition-colors hover:text-[#173CAE]"
        >
          Browse the catalogue <ArrowRight size={16} />
        </Link>
      </motion.div>
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {featuredProducts.map((p, i) => (
          <motion.div
            key={p.id}
            {...fadeUp}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow duration-300 hover:shadow-xl"
            data-testid={`featured-product-${p.id}`}
          >
            <div className="aspect-[4/3] overflow-hidden bg-slate-50">
              <img
                src={p.image}
                alt={p.brand}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-5">
              <p className="text-base font-extrabold tracking-wide text-slate-900">{p.brand}</p>
              <p className="mt-1 text-xs leading-snug text-slate-500">{p.composition}</p>
              <span className="mt-3 inline-block rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold text-[#1D4ED8]">
                {p.category}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </section>

    {/* Therapeutic areas bento */}
    <section className="bg-[#0A1633] py-24 text-white" data-testid="home-therapies">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} className="max-w-2xl">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-blue-300">Where We Care</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
            Expanding across therapeutic frontiers
          </h2>
          <p className="mt-4 text-slate-300">
            From oncology and speciality formulations to general pharma — steadily growing into every therapy area that matters.
          </p>
        </motion.div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {THERAPIES.slice(0, 6).map((t, i) => (
            <motion.div
              key={t.name}
              {...fadeUp}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className="rounded-2xl border border-white/10 bg-white/5 p-7 backdrop-blur-sm transition-colors duration-300 hover:bg-white/10"
              data-testid={`therapy-card-${i}`}
            >
              <Pill size={20} className="text-blue-300" />
              <h3 className="mt-4 text-lg font-bold">{t.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{t.focus}</p>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-wider text-blue-300/80">{t.molecules}</p>
            </motion.div>
          ))}
        </div>
        <motion.div {...fadeUp} className="mt-10">
          <Link
            to="/therapeutic-areas"
            data-testid="therapies-explore-link"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold transition-colors duration-200 hover:bg-white/10"
          >
            Explore therapeutic areas <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>

    {/* Vision 2030 teaser */}
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="home-vision-teaser">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <motion.div {...fadeUp}>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#1D4ED8]">Vision 2030</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Towards a pan-India pharmaceutical house
          </h2>
          <p className="mt-5 leading-relaxed text-slate-600">
            By 2030, Cytocare aims to be one of the largest and leading pharmaceutical houses in
            Eastern India with a pan-India presence — creating a more accessible and affordable
            healthcare ecosystem with value and ethics in all respect.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/vision-2030"
              data-testid="vision-teaser-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#173CAE]"
            >
              <Target size={16} /> Our roadmap to 2030
            </Link>
          </div>
        </motion.div>
        <motion.div {...fadeUp} className="grid grid-cols-2 gap-4">
          {[
            { icon: FlaskConical, label: "New Segments", text: "Dermatology, geriatric & palliative care" },
            { icon: Building2, label: "Pan-India Reach", text: "Beyond Eastern India, nationwide" },
            { icon: Sparkles, label: "Affordability", text: "Accessible pricing for every patient" },
            { icon: HeartHandshake, label: "Ethics First", text: "Transparent, patient-centric growth" },
          ].map((c) => (
            <div key={c.label} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <c.icon size={20} className="text-[#1D4ED8]" />
              <p className="mt-3 text-sm font-bold text-slate-900">{c.label}</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">{c.text}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  </div>
);

export default Home;
